import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { Calendar, Clock, AlertCircle, Loader2, BookOpen, CheckCircle2, CheckCircle, XCircle } from 'lucide-react';

interface EventConfig {
  id: number;
  title: string;
  slot_duration_minutes: number;
  public_token: string;
  teacher_id: number;
  first_name: string | null;
  last_name: string | null;
  username?: string;
  teacher_display_name?: string;
  available_slots_count: number;
}

interface Slot {
  id: number;
  config_id: number;
  start_time: string;
  end_time: string;
  status: string;
  config_title: string;
  slot_duration_minutes: number;
  teacher_id: number;
  teacher_first_name: string;
  teacher_last_name: string;
}

interface EventData {
  event: {
    id: number;
    title: string;
    description: string | null;
    start_time: string;
    end_time: string;
    slot_duration_minutes: number;
  };
  configs: EventConfig[];
}

export default function AppointmentsPublicEvent() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [selectedTeachers, setSelectedTeachers] = useState<number[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<number[]>([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    parent_name: '',
    parent_email: '',
    student_name: '',
    student_id: '' as string,
    notes: ''
  });
  const [emailKnown, setEmailKnown] = useState<boolean | null>(null);
  const [studentSuggestions, setStudentSuggestions] = useState<{ id: string; name: string; klasse: string }[]>([]);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const studentDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bookingForm.parent_email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingForm.parent_email)) {
      setEmailKnown(null);
      return;
    }
    const t = setTimeout(() => {
      api.get(`/appointments/public/check-email?email=${encodeURIComponent(bookingForm.parent_email)}`)
        .then((res) => setEmailKnown(res.data?.data?.known ?? false))
        .catch(() => setEmailKnown(null));
    }, 500);
    return () => clearTimeout(t);
  }, [bookingForm.parent_email]);

  useEffect(() => {
    if (bookingForm.student_name.trim().length < 2 || !emailKnown || !bookingForm.parent_email?.trim()) {
      setStudentSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      const q = encodeURIComponent(bookingForm.student_name);
      const email = encodeURIComponent(bookingForm.parent_email);
      api.get(`/appointments/public/search-students?q=${q}&email=${email}`)
        .then((res) => setStudentSuggestions(res.data?.data ?? []))
        .catch(() => setStudentSuggestions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [bookingForm.student_name, bookingForm.parent_email, emailKnown]);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(e.target as Node)) {
        setShowStudentDropdown(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const { data, isLoading: eventLoading, error } = useQuery<EventData>(
    ['appointments', 'public', 'event', token],
    async () => {
      const response = await api.get(`/appointments/public/event/${token}`);
      return response.data.data;
    },
    { enabled: !!token, retry: 1 }
  );

  const { data: slots, isLoading: slotsLoading } = useQuery<Slot[]>(
    ['appointments', 'public', 'event', token, 'slots', selectedTeachers.join(',')],
    async () => {
      if (!token || selectedTeachers.length === 0) return [];
      const response = await api.get(
        `/appointments/public/event/${token}/slots?config_ids=${selectedTeachers.join(',')}`
      );
      return response.data.data;
    },
    { enabled: !!token && selectedTeachers.length > 0 }
  );

  const bookMutation = useMutation(
    async (payload: {
      slot_ids: number[];
      parent_name: string;
      parent_email: string;
      student_name: string;
      student_id?: string;
      notes?: string;
    }) => {
      const response = await api.post(`/appointments/public/event/${token}/book`, payload);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setSelectedTeachers([]);
        setSelectedSlots([]);
        setShowBookingForm(false);
        setBookingForm({ parent_name: '', parent_email: '', student_name: '', student_id: '', notes: '' });
        setEmailKnown(null);
        setStudentSuggestions([]);
        if (data?.emailKnown === false) {
          toast.success('Termine gebucht. Hinweis: Die E-Mail-Adresse ist nicht in der Liste hinterlegter Adressen.');
        } else {
          toast.success(data?.message || 'Termine gebucht. Bitte bestätigen Sie jede Buchung per E-Mail-Link.');
        }
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Fehler bei der Buchung');
      }
    }
  );

  const groupedSlots = useMemo(() => {
    if (!slots || slots.length === 0) return {};
    const grouped: Record<string, Record<number, Slot[]>> = {};
    slots.forEach((slot) => {
      const start = parseISO(slot.start_time);
      const timeKey = format(start, 'yyyy-MM-dd HH:mm');
      if (!grouped[timeKey]) grouped[timeKey] = {};
      if (!grouped[timeKey][slot.config_id]) grouped[timeKey][slot.config_id] = [];
      grouped[timeKey][slot.config_id].push(slot);
    });
    return grouped;
  }, [slots]);

  const timeSlots = useMemo(() => Object.keys(groupedSlots).sort(), [groupedSlots]);

  const handleTeacherToggle = (configId: number) => {
    setSelectedTeachers((prev) => {
      if (prev.includes(configId)) {
        const teacherSlots = slots?.filter((s) => s.config_id === configId) || [];
        setSelectedSlots((prevSlots) =>
          prevSlots.filter((id) => !teacherSlots.some((s) => s.id === id))
        );
        return prev.filter((id) => id !== configId);
      }
      return [...prev, configId];
    });
  };

  const handleSlotToggle = (slotId: number, configId: number) => {
    setSelectedSlots((prev) => {
      const teacherSlots = slots?.filter((s) => s.config_id === configId) || [];
      const filtered = prev.filter((id) => !teacherSlots.some((s) => s.id === id));
      if (!prev.includes(slotId)) return [...filtered, slotId];
      return filtered;
    });
  };

  const getSelectedSlotForTeacher = (configId: number) => {
    const teacherSlots = slots?.filter((s) => s.config_id === configId) || [];
    return selectedSlots.find((id) => teacherSlots.some((s) => s.id === id));
  };

  const getTeacherDisplayName = (c: EventConfig) => {
    if (c.teacher_display_name && c.teacher_display_name.trim()) return c.teacher_display_name.trim();
    const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
    return name || c.username || 'Unbekannt';
  };

  const selectedTeachersData = useMemo(
    () => data?.configs?.filter((c) => selectedTeachers.includes(c.id)) || [],
    [data?.configs, selectedTeachers]
  );

  if (eventLoading || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !data || !data.configs?.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Anlass nicht gefunden</h1>
          <p className="text-gray-600 mb-4">
            Der Link ist ungültig oder es sind keine Termine mehr verfügbar.
          </p>
          <button
            onClick={() => navigate('/appointments/all')}
            className="text-indigo-600 hover:underline"
          >
            Alle Terminbuchungen anzeigen
          </button>
        </div>
      </div>
    );
  }

  const { event, configs } = data;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">{event.title}</h1>
          {event.description && (
            <p className="text-gray-600 mb-2">{event.description}</p>
          )}
          <p className="text-sm text-gray-500">
            {event.slot_duration_minutes} Min pro Termin
          </p>
        </div>

        {/* Lehrer auswählen */}
        <div className="mb-8">
          <label className="block text-sm font-medium mb-2">Lehrer auswählen:</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {configs.map((config) => (
              <label
                key={config.id}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  selectedTeachers.includes(config.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedTeachers.includes(config.id)}
                    onChange={() => handleTeacherToggle(config.id)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-semibold">{getTeacherDisplayName(config)}</div>
                    <div className="text-sm text-gray-600">{config.title}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {config.available_slots_count} verfügbare Termine
                    </div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Matrix: Verfügbare Termine auswählen */}
        {selectedTeachers.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4">Verfügbare Termine auswählen</h3>
            <p className="text-sm text-gray-500 mb-3 md:hidden">
              Tipp: Bei vielen Lehrern nach links/rechts wischen.
            </p>
            {slotsLoading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
              </div>
            ) : slots && slots.length > 0 && timeSlots.length > 0 ? (
              <div className="overflow-x-auto overflow-y-auto max-h-[70vh] rounded-lg border border-gray-200 bg-white">
                <table className="border-collapse w-auto table-auto min-w-max">
                  <thead className="sticky top-0 z-10 bg-slate-600 text-white shadow-md">
                    <tr>
                      <th className="sticky left-0 z-20 border border-slate-500 bg-slate-600 p-3 text-left font-semibold min-w-[140px] whitespace-nowrap">
                        Zeit
                      </th>
                      {selectedTeachersData.map((teacher) => (
                        <th
                          key={teacher.id}
                          className="border border-slate-500 p-3 text-center font-semibold min-w-[140px] whitespace-nowrap"
                        >
                          {getTeacherDisplayName(teacher)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((timeKey) => {
                      const timeSlot = parseISO(timeKey);
                      return (
                        <tr key={timeKey}>
                          <td className="sticky left-0 z-[5] border bg-white p-2 font-semibold whitespace-nowrap">
                            {format(timeSlot, 'dd.MM.yyyy HH:mm', { locale: de })}
                          </td>
                          {selectedTeachersData.map((teacher) => {
                            const teacherSlots = groupedSlots[timeKey]?.[teacher.id] || [];
                            const selectedSlotForTeacher = getSelectedSlotForTeacher(teacher.id);
                            return (
                              <td key={teacher.id} className="border p-2 text-center">
                                {teacherSlots.length > 0 ? (
                                  teacherSlots.map((slot) => {
                                    const isSelected = selectedSlotForTeacher === slot.id;
                                    return (
                                      <div
                                        key={slot.id}
                                        onClick={() => handleSlotToggle(slot.id, teacher.id)}
                                        className={`p-3 rounded-lg cursor-pointer transition-all duration-200 mb-2 ${
                                          isSelected
                                            ? 'bg-green-500 text-white shadow-md scale-105'
                                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                        }`}
                                      >
                                        <div className="font-semibold">
                                          {format(parseISO(slot.start_time), 'HH:mm', { locale: de })}-
                                          {format(parseISO(slot.end_time), 'HH:mm', { locale: de })}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {slots && slots.length > 0 && timeSlots.length === 0
                  ? 'Keine zukünftigen Termine für die ausgewählten Lehrer.'
                  : 'Keine verfügbaren Termine für die ausgewählten Lehrer.'}
              </div>
            )}

            {selectedSlots.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={() => setShowBookingForm(true)}
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
                >
                  <BookOpen className="w-5 h-5" />
                  Jetzt buchen ({selectedSlots.length} Termin{selectedSlots.length > 1 ? 'e' : ''})
                </button>
              </div>
            )}
          </div>
        )}

        <div className="text-center">
          <button
            onClick={() => navigate('/appointments/all')}
            className="text-gray-600 hover:underline text-sm"
          >
            Alle Terminbuchungen anzeigen
          </button>
        </div>
      </div>

      {/* Buchungs-Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">Buchungsdetails</h3>
            <p className="text-sm text-gray-600 mb-4">
              Sie erhalten eine E-Mail mit einem Link <strong>„Alle Termine auf einmal bestätigen“</strong>. Alternativ können Sie jeden Termin einzeln bestätigen. Bitte innerhalb von 10 Minuten.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Stornierung: Nach der Bestätigung erhalten Sie eine E-Mail mit Storno-Links (einzeln und alle).
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Name des Elternteils <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={bookingForm.parent_name}
                  onChange={(e) => setBookingForm({ ...bookingForm, parent_name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  E-Mail-Adresse <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={bookingForm.parent_email}
                  onChange={(e) => setBookingForm({ ...bookingForm, parent_email: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
                {emailKnown === true && (
                  <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> E-Mail als Erziehungsberechtigten-Adresse erkannt
                  </p>
                )}
                {emailKnown === false && (
                  <p className="mt-1 text-sm text-amber-600 flex items-center gap-1">
                    <XCircle className="w-4 h-4" /> Nicht in der Liste hinterlegter Adressen (Termin wird trotzdem gebucht)
                  </p>
                )}
              </div>
              <div className="relative" ref={studentDropdownRef}>
                <label className="block text-sm font-medium mb-1">
                  Name des Schülers <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-1">
                  {emailKnown === true
                    ? 'Zur Zuordnung wird der Name mit der Schülerliste abgeglichen (nur Ihre Kinder, datenschutzkonform). Sie können auswählen oder eintippen.'
                    : 'Bei bekannter E-Mail wird der Name automatisch mit der Schülerliste abgeglichen.'}
                </p>
                <input
                  type="text"
                  value={bookingForm.student_name}
                  onChange={(e) => {
                    setBookingForm({ ...bookingForm, student_name: e.target.value, student_id: '' });
                    setShowStudentDropdown(true);
                  }}
                  onFocus={() => emailKnown === true && studentSuggestions.length > 0 && setShowStudentDropdown(true)}
                  className="w-full border rounded px-3 py-2"
                  placeholder={emailKnown === true ? 'Name eintippen oder aus der Liste wählen...' : 'Name des Schülers eingeben'}
                  required
                />
                {emailKnown === true && showStudentDropdown && studentSuggestions.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
                    {studentSuggestions.map((s) => (
                      <li
                        key={s.id}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
                        onClick={() => {
                          setBookingForm({ ...bookingForm, student_name: s.name, student_id: s.id });
                          setShowStudentDropdown(false);
                          setStudentSuggestions([]);
                        }}
                      >
                        <span className="font-medium">{s.name}</span>
                        {s.klasse && <span className="text-gray-500 text-sm ml-2">({s.klasse})</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notizen</label>
                <textarea
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowBookingForm(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => {
                    if (!bookingForm.parent_name || !bookingForm.parent_email) {
                      toast.error('Bitte Name des Elternteils und E-Mail ausfüllen');
                      return;
                    }
                    if (!bookingForm.student_name?.trim()) {
                      toast.error('Bitte Name des Schülers angeben (zur Zuordnung des Termins)');
                      return;
                    }
                    bookMutation.mutate({
                      slot_ids: selectedSlots,
                      parent_name: bookingForm.parent_name,
                      parent_email: bookingForm.parent_email,
                      student_name: bookingForm.student_name,
                      student_id: bookingForm.student_id || undefined,
                      notes: bookingForm.notes || undefined
                    });
                  }}
                  disabled={bookMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {bookMutation.isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Wird gebucht...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Buchen
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
